// Copyright 2018 Sorama B.V.

import { VisualExport } from "./Export";
import { ImageFormat } from "./Format";

export abstract class BrowserExportBase implements VisualExport {
    private readonly maximumExportDimResolution: number;

    protected width: number;
    protected height: number;
    protected playbackRate = 1;

    private actualWidth: number;
    private actualHeight: number;

    constructor(maximumExportDimResolution: number) {
        this.maximumExportDimResolution = maximumExportDimResolution;
    }

    public fitResolution(width: number, height: number): VisualExport {
        this.width = width;
        this.height = height;
        return this;
    }

    public setPlaybackRate(playbackRate: number): VisualExport {
        this.playbackRate = playbackRate;
        return this;
    }

    public abstract export(fileName: string): Promise<void>;

    public abstract exportToBuffer(): Promise<ArrayBuffer>;

    protected rasterize(svgImage: string, imageFormat: ImageFormat): Promise<ArrayBuffer> {
        return new Promise((resolve, reject) => {
            // Find out the actual image size
            if (!this.actualWidth || !this.actualHeight) {
                const size = this.getImageSize(svgImage, this.width, this.height);

                this.actualWidth = size.width;
                this.actualHeight = size.height;

                // Also set the size to the actual size
                this.width = this.actualWidth;
                this.height = this.actualHeight;
            }

            // Generate a new image
            let imageEl = new Image();

            // Generate content for the image and wait for it to load
            const content = new Blob([this.sanitizeSvg(svgImage, this.width, this.height)], { type: "image/svg+xml" });

            // Generate content for the image and wait for it to load
            imageEl.src = URL.createObjectURL(content);
            imageEl.onload = () => {
                // Create a canvas element and draw the image
                let canvasEl = document.createElement("canvas");
                canvasEl.width = this.width;
                canvasEl.height = this.height;
                const context = canvasEl.getContext("2d");

                context.drawImage(imageEl, 0, 0);

                try {
                    canvasEl.toBlob((blob) => {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            // Set resources to null to allow cleanup
                            canvasEl = null;
                            imageEl = null;
                            resolve(<ArrayBuffer>reader.result);
                        };
                        reader.onerror = (e) => {
                            console.error(e);
                            reject(new Error("Unable to render image"));
                        };

                        reader.readAsArrayBuffer(blob);
                    }, "image/" + imageFormat.name.toLowerCase());
                } catch (e) {
                    console.error(e);
                    reject(new Error("Unable to render image" + e.message ? ", " + e.message : ""));
                }
            };

            imageEl.onerror = (e) => {
                console.error(e);
                reject(new Error("Unable to render image"));
            };
        });
    }

    private getImageSize(svgImage: string, width?: number, height?: number): { width: number; height: number } {
        // Find the original svg resolution
        const svgString = svgImage.match("<svg.*>");

        const parser = new DOMParser();
        const doc = parser.parseFromString(svgString + "</svg>", "text/xml");

        const viewbox = doc.documentElement.attributes.getNamedItem("viewBox").value;
        const viewboxDimensions = viewbox.split(",");

        const imageWidth = Number(viewboxDimensions[2]);
        const imageHeight = Number(viewboxDimensions[3]);

        if (!width && !height) {
            // No input defined, use the image size
            width = imageWidth;
            height = imageHeight;
        } else if (!width) {
            // Only height defined, use that to determine the width
            width = (height / imageHeight) * imageWidth;
        } else if (!height) {
            // Only width defined, use that to determine the height
            height = (width / imageWidth) * imageHeight;
        } else {
            // Both defined, determine which dimension is leading and how to fit within the provided bounding box
            const scale = Math.min(width / imageWidth, height / imageHeight);
            width = imageWidth * scale;
            height = imageHeight * scale;
        }

        // Now make sure we do not exceed the maximum allowed dimension
        const max = Math.max(width, height);
        if (max > this.maximumExportDimResolution) {
            const scale = this.maximumExportDimResolution / max;
            width *= scale;
            height *= scale;
        }

        // Round width and height to integer values
        width = Math.round(width);
        height = Math.round(height);

        // Make sure both dimensions are even numbers
        width -= width % 2;
        height -= height % 2;

        return { width: width, height: height };
    }

    private sanitizeSvg(svgImage: string, width: number, height: number): string {
        // Find svg header string
        const svgRegex = /<svg .*>/;
        const svgHeaderMatch = svgImage.match(svgRegex);
        let svgHeader = svgHeaderMatch[0];

        // Find all attributes in the svg header and check which ones to discard or update
        const attribMatch = /([a-zA-Z:-]*)=['"]([\S]*)['"]/g;
        const newSvgHeaderPairs: { [key: string]: string } = {
            xmlns: "http://www.w3.org/2000/svg",
            version: "1.1",
            "xmlns:xlink": "http://www.w3.org/1999/xlink",
            width: width.toFixed(0),
            height: height.toFixed(0),
        };

        let match: RegExpExecArray;
        do {
            match = attribMatch.exec(svgHeader);

            if (match) {
                const key = match[1].toLowerCase();

                // Check if we know this header
                if (
                    key === "xmlns" ||
                    key === "version" ||
                    key === "xmlns:xlink" ||
                    key === "width" ||
                    key === "height"
                ) {
                    // Header that we generate ourselves, skip
                    continue;
                }

                let value = match[2];
                if (key === "viewbox") {
                    // Viewbox attribute, make sure the aspect ratio of the viewbox exactly matches the width/height aspect ratio
                    const splits = value.split(",");
                    const vbWidth = Number(splits[2]);
                    let vbHeight = Number(splits[3]);

                    vbHeight = (vbWidth / width) * height;
                    value = splits[0] + "," + splits[1] + "," + vbWidth.toFixed(4) + "," + vbHeight.toFixed(4);
                }

                newSvgHeaderPairs[match[1]] = value;
            }
        } while (match);

        if (!newSvgHeaderPairs["viewBox"]) {
            newSvgHeaderPairs["viewBox"] = "0,0," + width.toFixed(0) + "," + height.toFixed(0);
        }

        // Generate header
        svgHeader = "<svg";
        for (const key in newSvgHeaderPairs) {
            svgHeader += " " + key + "=" + "'" + newSvgHeaderPairs[key] + "'";
        }
        svgHeader += ">";

        const doctype =
            // eslint-disable-next-line quotes
            '<?xml version="1.0" standalone="no"?><!DOCTYPE svg PUBLIC "-//W3C//DTD SVG 1.1//EN" "http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd" [<!ENTITY nbsp "&#160;">]>';
        return doctype + svgImage.replace(svgRegex, svgHeader);
    }
}
